import {
  buildNormalizedDay,
  buildNormalizedPilotBlueprint,
  localizedGenderNode,
} from "./referenceNormalizerPilotUtils";

/** Required substring for scripts/validate_dna_blueprints.mjs (runtime writing steps). */
export const DNA_VALIDATE_WRITING_PHRASE = "אני שואל/ת את עצמי בשקט";

const days = [
  {
    dayNumber: 1,
    emotionalStage: "pause",
    title: "Naming the Fear (and Not Running From It)",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Today I stop ignoring the fear.",
      "I don’t try to calm it — I listen to it."
    ],
      [
      "Daily intention:",
      "Today I stop ignoring the fear.",
      "I don’t try to calm it — I listen to it."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One slow breath.",
      "And I say in my heart:",
      "“This is fear. It is not a prophecy.”"
    ],
      [
      "I pause with myself.",
      "One slow breath.",
      "And I say in my heart:",
      "“This is fear. It is not a prophecy.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing — for myself\nI ask myself:\nWhat is the exact sentence that scares me?\n(“I will not find love again”, “Something is wrong with me”, “Everyone found love except me”)",
      "I ask myself:\nWhere does this fear sit in my body?\n(chest / stomach / throat / shoulders)",
      "I ask myself:\nWhen does this fear appear most strongly?\n(at night? on dates? when I see others?)"
    ],
      [
      "Writing — for myself\nI ask myself:\nWhat is the exact sentence that scares me?\n(“I will not find love again”, “Something is wrong with me”, “Everyone found love except me”)",
      "I ask myself:\nWhere does this fear sit in my body?\n(chest / stomach / throat / shoulders)",
      "I ask myself:\nWhen does this fear appear most strongly?\n(at night? on dates? when I see others?)"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Fear does not mean truth.",
      "Fear says: “I hurt, and I need safety.”"
    ],
      [
      "Closing",
      "Fear does not mean truth.",
      "Fear says: “I hurt, and I need safety.”"
    ]
    ),
    action: localizedGenderNode(
      "Once today — a hand on my body + the sentence:",
      "Once today — a hand on my body + the sentence:"
    ),
  },
  {
    dayNumber: 2,
    emotionalStage: "pain",
    title: "Fear as a Voice, Not a Fact",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Today I learn to tell the difference between an inner voice and reality."
    ],
      [
      "Daily intention:",
      "Today I learn to tell the difference between an inner voice and reality."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause.",
      "One breath.",
      "“Not every thought is true.”"
    ],
      [
      "I pause.",
      "One breath.",
      "“Not every thought is true.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat does the fear say about my future?",
      "What true fact do I have right now — without interpretation?",
      "What part of me is speaking through this fear?\n(the child who was abandoned, the part that was disappointed, the tired part)",
      "What one gentle truth can I choose today instead of the frightening voice?\n(one sentence only)"
    ],
      [
      "Writing\nWhat does the fear say about my future?",
      "What true fact do I have right now — without interpretation?",
      "What part of me is speaking through this fear?\n(the child who was abandoned, the part that was disappointed, the tired part)",
      "What one gentle truth can I choose today instead of the frightening voice?\n(one sentence only)"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "I don’t have to believe every voice that passes through me."
    ],
      [
      "Closing",
      "I don’t have to believe every voice that passes through me."
    ]
    ),
    action: localizedGenderNode(
      "When the frightening sentence comes up — I pause and breathe once before I continue.",
      "When the frightening sentence comes up — I pause and breathe once before I continue."
    ),
  },
  {
    dayNumber: 3,
    emotionalStage: "pain",
    title: "Where This Fear Was Born",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Today I understand that the fear did not start today."
    ],
      [
      "Daily intention:",
      "Today I understand that the fear did not start today."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath."
    ],
      [
      "I pause with myself.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhen did I feel a similar feeling in the past?\n(a previous relationship, childhood, an experience of rejection)",
      "What did I learn then about love?",
      "Does that rule still have to be true today?",
      "What part of me still acts by an old rule?"
    ],
      [
      "Writing\nWhen did I feel a similar feeling in the past?\n(a previous relationship, childhood, an experience of rejection)",
      "What did I learn then about love?",
      "Does that rule still have to be true today?",
      "What part of me still acts by an old rule?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "What I learned then does not have to run my life today."
    ],
      [
      "Closing",
      "What I learned then does not have to run my life today."
    ]
    ),
    action: localizedGenderNode(
      "I write to myself:",
      "I write to myself:"
    ),
  },
  {
    dayNumber: 4,
    emotionalStage: "pattern",
    title: "Comparison to Others (and the Lie in It)",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Today I stop measuring myself by the pace of others."
    ],
      [
      "Daily intention:",
      "Today I stop measuring myself by the pace of others."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause.",
      "One breath.",
      "“My journey does not have to look like anyone else’s.”"
    ],
      [
      "I pause.",
      "One breath.",
      "“My journey does not have to look like anyone else’s.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWho do I compare myself to?",
      "What do I imagine they have that I don’t?",
      "What don’t I see in their journey?",
      "What one truth about my own path am I willing to remember today?"
    ],
      [
      "Writing\nWho do I compare myself to?",
      "What do I imagine they have that I don’t?",
      "What don’t I see in their journey?",
      "What one truth about my own path am I willing to remember today?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Comparison does not build love — it only wears down the heart."
    ],
      [
      "Closing",
      "Comparison does not build love — it only wears down the heart."
    ]
    ),
    action: localizedGenderNode(
      "Today I avoid one comparison on purpose.",
      "Today I avoid one comparison on purpose."
    ),
  },
  {
    dayNumber: 5,
    emotionalStage: "pattern",
    title: "Shame (“Something Is Wrong With Me”)",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Today I touch shame without letting it define me."
    ],
      [
      "Daily intention:",
      "Today I touch shame without letting it define me."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause.",
      "One breath.",
      "“Shame does not mean truth. It means I’m hurting.”"
    ],
      [
      "I pause.",
      "One breath.",
      "“Shame does not mean truth. It means I’m hurting.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat is the thing I feel ashamed of about relationships?",
      "If a beloved friend said this about themselves — what would I tell them?",
      "How does that same sentence sound in a human, gentle version?",
      "What one compassion sentence do I choose for myself today?"
    ],
      [
      "Writing\nWhat is the thing I feel ashamed of about relationships?",
      "If a beloved friend said this about themselves — what would I tell them?",
      "How does that same sentence sound in a human, gentle version?",
      "What one compassion sentence do I choose for myself today?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Shame wants me to hide.",
      "I choose to stay."
    ],
      [
      "Closing",
      "Shame wants me to hide.",
      "I choose to stay."
    ]
    ),
    action: localizedGenderNode(
      "A hand on my chest + my compassion sentence once only.",
      "A hand on my chest + my compassion sentence once only."
    ),
  },
  {
    dayNumber: 6,
    emotionalStage: "fear",
    title: "Breaking the “One Love” Myth",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Today I release the idea that there was only one chance."
    ],
      [
      "Daily intention:",
      "Today I release the idea that there was only one chance."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause.",
      "One breath."
    ],
      [
      "I pause.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhere did the idea that there is “one love” come from?",
      "What proof do I have that it is really true?",
      "What wider truth exists about relationships and people?",
      "What does it mean about me if the next love is different — not a copy?"
    ],
      [
      "Writing\nWhere did the idea that there is “one love” come from?",
      "What proof do I have that it is really true?",
      "What wider truth exists about relationships and people?",
      "What does it mean about me if the next love is different — not a copy?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Love does not end because one relationship ended."
    ],
      [
      "Closing",
      "Love does not end because one relationship ended."
    ]
    ),
    action: localizedGenderNode(
      "I write:",
      "I write:"
    ),
  },
  {
    dayNumber: 7,
    emotionalStage: "need",
    title: "Pausing in the Middle of a Prophecy",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Today I practice stopping a prophecy before it becomes identity."
    ],
      [
      "Daily intention:",
      "Today I practice stopping a prophecy before it becomes identity."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause.",
      "One breath."
    ],
      [
      "I pause.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat prophecy do I run about myself?",
      "What harm do I do to myself when I believe it?",
      "What other possibility exists — even if I don’t feel it right now?",
      "What stop sentence do I want to adopt?"
    ],
      [
      "Writing\nWhat prophecy do I run about myself?",
      "What harm do I do to myself when I believe it?",
      "What other possibility exists — even if I don’t feel it right now?",
      "What stop sentence do I want to adopt?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "The future is not written by fear — it is built from steps."
    ],
      [
      "Closing",
      "The future is not written by fear — it is built from steps."
    ]
    ),
    action: localizedGenderNode(
      "When a prophecy rises — I say my stop sentence.",
      "When a prophecy rises — I say my stop sentence."
    ),
  },
  {
    dayNumber: 8,
    emotionalStage: "softening",
    title: "What I Do Look For in Love",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Today I move the focus from fear — to value."
    ],
      [
      "Daily intention:",
      "Today I move the focus from fear — to value."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause.",
      "One breath."
    ],
      [
      "I pause.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat really matters to me in a relationship?",
      "What need of mine did not have a place in the past?",
      "What one value do I want to lead me from now on?",
      "How can I give myself a little of that today?"
    ],
      [
      "Writing\nWhat really matters to me in a relationship?",
      "What need of mine did not have a place in the past?",
      "What one value do I want to lead me from now on?",
      "How can I give myself a little of that today?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Love begins in the relationship I build with myself."
    ],
      [
      "Closing",
      "Love begins in the relationship I build with myself."
    ]
    ),
    action: localizedGenderNode(
      "Today I do one action that honors the value I chose.",
      "Today I do one action that honors the value I chose."
    ),
  },
  {
    dayNumber: 9,
    emotionalStage: "softening",
    title: "Fear of Dating (and Being Seen)",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Today I touch the fear without closing up."
    ],
      [
      "Daily intention:",
      "Today I touch the fear without closing up."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause.",
      "One breath."
    ],
      [
      "I pause.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat is most frightening to me about dating?",
      "What do I fear they will see in me?",
      "What do I forget about my right to be myself?",
      "What one truth do I want to remember before a human meeting?"
    ],
      [
      "Writing\nWhat is most frightening to me about dating?",
      "What do I fear they will see in me?",
      "What do I forget about my right to be myself?",
      "What one truth do I want to remember before a human meeting?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "I don’t have to impress to be worthy."
    ],
      [
      "Closing",
      "I don’t have to impress to be worthy."
    ]
    ),
    action: localizedGenderNode(
      "If there is a meeting — I arrive as I am, without additions.",
      "If there is a meeting — I arrive as I am, without additions."
    ),
  },
  {
    dayNumber: 10,
    emotionalStage: "perspective",
    title: "Patience as an Emotional Quality",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Today I practice patience without giving up on myself."
    ],
      [
      "Daily intention:",
      "Today I practice patience without giving up on myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause.",
      "One breath."
    ],
      [
      "I pause.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhere am I rushing too much?",
      "What is the cost of this pressure?",
      "What does patience look like that doesn’t give up on life?",
      "What pace is right for me right now?"
    ],
      [
      "Writing\nWhere am I rushing too much?",
      "What is the cost of this pressure?",
      "What does patience look like that doesn’t give up on life?",
      "What pace is right for me right now?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Pace does not mean stuck.",
      "Pace means listening."
    ],
      [
      "Closing",
      "Pace does not mean stuck.",
      "Pace means listening."
    ]
    ),
    action: localizedGenderNode(
      "I choose to do one thing today without hurrying.",
      "I choose to do one thing today without hurrying."
    ),
  },
  {
    dayNumber: 11,
    emotionalStage: "strength",
    title: "Restoring Control (What Is in My Hands)",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Today I reduce fear into action."
    ],
      [
      "Daily intention:",
      "Today I reduce fear into action."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause.",
      "One breath."
    ],
      [
      "I pause.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat is not in my control — and I release today?",
      "What is in my control? (3 small things)",
      "What one step only will I take this week?"
    ],
      [
      "Writing\nWhat is not in my control — and I release today?",
      "What is in my control? (3 small things)",
      "What one step only will I take this week?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing"
    ],
      [
      "Closing"
    ]
    ),
    action: localizedGenderNode(
      "Small action",
      "Small action"
    ),
  },
  {
    dayNumber: 12,
    emotionalStage: "release",
    title: "Loving Even When Afraid",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Today I make room for two feelings together."
    ],
      [
      "Daily intention:",
      "Today I make room for two feelings together."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause.",
      "One breath."
    ],
      [
      "I pause.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nHow can fear and love exist together in me?",
      "What part of me wants connection — even while it is afraid?",
      "What can I promise myself within a future relationship?"
    ],
      [
      "Writing\nHow can fear and love exist together in me?",
      "What part of me wants connection — even while it is afraid?",
      "What can I promise myself within a future relationship?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Courage does not mean there is no fear.",
      "Courage means I don’t let it stop me."
    ],
      [
      "Closing",
      "Courage does not mean there is no fear.",
      "Courage means I don’t let it stop me."
    ]
    ),
    action: localizedGenderNode(
      "I write one promise to myself.",
      "I write one promise to myself."
    ),
  },
  {
    dayNumber: 13,
    emotionalStage: "choice",
    title: "Realistic Hope",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Today I build hope that does not depend on fantasy."
    ],
      [
      "Daily intention:",
      "Today I build hope that does not depend on fantasy."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause.",
      "One breath."
    ],
      [
      "I pause.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat is real hope for me?",
      "What do I stop waiting to happen “before”?",
      "What does a good life look like even now?"
    ],
      [
      "Writing\nWhat is real hope for me?",
      "What do I stop waiting to happen “before”?",
      "What does a good life look like even now?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Hope is built while living, not before."
    ],
      [
      "Closing",
      "Hope is built while living, not before."
    ]
    ),
    action: localizedGenderNode(
      "I do one thing that connects me to life today.",
      "I do one thing that connects me to life today."
    ),
  },
  {
    dayNumber: 14,
    emotionalStage: "continuation",
    title: "Soft Closing and Opening a Path",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Today I don’t promise love — I promise presence."
    ],
      [
      "Daily intention:",
      "Today I don’t promise love — I promise presence."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath."
    ],
      [
      "I pause with myself.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nThree things I stop saying to myself about love:",
      "Three things I begin to believe:",
      "A path sentence for hard days:"
    ],
      [
      "Writing\nThree things I stop saying to myself about love:",
      "Three things I begin to believe:",
      "A path sentence for hard days:"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Final closing for the journey",
      "I may not know when love will arrive.",
      "But I know who I am when I meet it.",
      "And I stay with myself —",
      "slowly, quietly, in presence.",
      "And that is strength."
    ],
      [
      "Final closing for the journey",
      "I may not know when love will arrive.",
      "But I know who I am when I meet it.",
      "And I stay with myself —",
      "slowly, quietly, in presence.",
      "And that is strength."
    ]
    ),
    action: localizedGenderNode(
      "I choose one action that symbolizes openness to life — without pressure.",
      "I choose one action that symbolizes openness to life — without pressure."
    ),
  },
].map(buildNormalizedDay);

export const fearNeverFindLoveAgainBlueprintEn = buildNormalizedPilotBlueprint({
  id: "fear_never_find_love_again_14d",
  version: "tri_locale_emit_en_v1",
  durationDays: 14,
  problemKey: "fear_never_find_love_again",
  category: "general",
  journeyName: {
    he: "פחד “אני לא אמצא שוב אהבה”",
    en: "Fear: “I Will Not Find Love Again”",
    es: "Miedo: “No volveré a encontrar el amor”",
  },
  days,
});

export default fearNeverFindLoveAgainBlueprintEn;
